// Exports (F-13), the same formats as the apps' PFStore Exports: expenses CSV, calendar ICS, places
// KML, track GPX and the `pathfinder-export` v1 JSON that is also the backup and restores.
import type { RowSnapshot } from '@pf/api-types';
import { MAX_PUSH_CHANGES, SCHEMA_VERSION } from '@pf/api-types';
import { exponent, format } from '@pf/money';
import { stampNow } from '../clock.ts';
import type { Edit } from '../sync.ts';
import { amountIn, categoryName, status, type TripModel } from './trip.ts';

const csvCell = (v: string | null | undefined) => {
  const s = v ?? '';
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function expensesCSV(m: TripModel, people: Record<string, string>): string {
  const name = (id: string) => people[id] ?? id;
  const minor = (n: number | null, c: string) =>
    n === null ? '' : (n / 10 ** exponent(c)).toFixed(exponent(c));
  let out = 'date,time,description,category,amount,currency,status,paid_by,split,personal,PHP,EUR\n';
  const sorted = [...m.entries].sort((a, b) =>
    `${a.dateLocal ?? ''}${a.timeLocal ?? ''}`.localeCompare(`${b.dateLocal ?? ''}${b.timeLocal ?? ''}`),
  );
  for (const e of sorted) {
    out += `${[
      e.dateLocal,
      e.timeLocal,
      e.description,
      categoryName(m, e.categoryId),
      minor(e.amountMinor, e.currency),
      e.currency,
      status(e),
      e.payers.map((p) => `${name(p.userId)} ${format(p.amountMinor, e.currency)}`).join('; '),
      e.splits.map((x) => `${name(x.userId)} ${format(x.amountMinor, e.currency)}`).join('; '),
      e.ownerUserId ? 'yes' : 'no',
      minor(amountIn(e, 'PHP'), 'PHP'),
      minor(amountIn(e, 'EUR'), 'EUR'),
    ]
      .map(csvCell)
      .join(',')}\n`;
  }
  return out;
}

/** "2026-11-06T09:30" plus `minutes`, as wall time (no zone needed). */
function addMinutes(local: string, minutes: number): string {
  const d = new Date(`${local.slice(0, 16)}:00Z`);
  d.setUTCMinutes(d.getUTCMinutes() + minutes);
  return d.toISOString().slice(0, 16);
}

/** Bookings and timed items as events in their own zones (TZID), so calendars show local times. */
export function ics(m: TripModel): string {
  const stamp = (local: string) => `${local.slice(0, 16).replace(/[-:]/g, '')}00`;
  const text = (s: string) =>
    s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  const now = `${stampNow().slice(0, 19).replace(/[-:]/g, '')}Z`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Pathfinder//Trip//EN',
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${text(m.title)}`,
  ];
  const all = [...m.days.flatMap((d) => d.items), ...m.lists.flatMap((l) => l.items)].flatMap((p) => [
    p,
    ...p.subItems,
  ]);
  for (const p of all) {
    const r = m.reservations.find((x) => x.itemId === p.item.id);
    const start = r?.startLocal ?? p.item.startLocal;
    if (!start || start.length < 16) continue;
    const tz = r?.startTz ?? p.item.tz ?? 'UTC';
    const end =
      r?.endLocal && r.endLocal.length >= 16 ? r.endLocal : addMinutes(start, p.item.durationMin ?? 60);
    lines.push(
      'BEGIN:VEVENT',
      `UID:${p.item.id}@pathfinder`,
      `DTSTAMP:${now}`,
      `SUMMARY:${text(p.item.title)}`,
      `DTSTART;TZID=${tz}:${stamp(start)}`,
      `DTEND;TZID=${r?.endTz ?? tz}:${stamp(end)}`,
    );
    if (p.item.place?.address) lines.push(`LOCATION:${text(p.item.place.address)}`);
    if (p.item.place?.lat != null && p.item.place.lng != null)
      lines.push(`GEO:${p.item.place.lat};${p.item.place.lng}`);
    if (r?.confirmation) lines.push(`DESCRIPTION:${text(`Confirmation: ${r.confirmation}`)}`);
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

const xml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function kml(m: TripModel): string {
  let out = `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${xml(m.title)}</name>\n`;
  const mark = (name: string, lat: number, lng: number) =>
    `<Placemark><name>${xml(name)}</name><Point><coordinates>${lng},${lat}</coordinates></Point></Placemark>\n`;
  for (const d of m.days) {
    out += `<Folder><name>${xml(d.date)}${d.title ? ` · ${xml(d.title)}` : ''}</name>\n`;
    for (const p of d.items.flatMap((x) => [x, ...x.subItems]))
      if (p.item.place?.lat != null && p.item.place.lng != null)
        out += mark([p.stopId, p.item.title].filter(Boolean).join(' '), p.item.place.lat, p.item.place.lng);
    out += '</Folder>\n';
  }
  for (const l of m.lists) {
    out += `<Folder><name>${xml(l.name)}</name>\n`;
    for (const p of l.items)
      if (p.item.place?.lat != null && p.item.place.lng != null)
        out += mark(p.item.title, p.item.place.lat, p.item.place.lng);
    out += '</Folder>\n';
  }
  return `${out}</Document></kml>\n`;
}

/** Google's encoded polyline at 1e6 precision (the apps' polyline6). */
export function decodePolyline6(s: string): [number, number][] {
  const out: [number, number][] = [];
  let i = 0;
  let lat = 0;
  let lng = 0;
  const next = () => {
    let shift = 0;
    let result = 0;
    let b: number;
    do {
      b = s.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < s.length) {
    lat += next();
    lng += next();
    out.push([lat / 1e6, lng / 1e6]);
  }
  return out;
}

/** My synced (simplified) track as GPX, one segment per recorded segment. The apps export raw fixes. */
export function gpx(m: TripModel, userId: string): string {
  let out = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="Pathfinder" xmlns="http://www.topografix.com/GPX/1/1">\n<trk><name>${xml(m.title)}</name>\n`;
  for (const seg of m.segments.filter((x) => x.userId === userId)) {
    out += '<trkseg>\n';
    for (const [lat, lng] of decodePolyline6(seg.polyline))
      out += `<trkpt lat="${lat}" lon="${lng}"></trkpt>\n`;
    out += '</trkseg>\n';
  }
  return `${out}</trk>\n</gpx>\n`;
}

// ── The JSON format (FR-EXP-01) ─────────────────────────────────────────────────────

export interface TripExport {
  format: 'pathfinder-export';
  version: number;
  schema: number;
  exportedAt: string;
  tripId: string;
  title: string;
  tables: Record<string, Record<string, string | number | boolean | null>[]>;
}

const DROPPED = new Set(['tripId', 'hlc', 'updatedAt', 'deletedAt']);

/** Every live row of the trip, by wire table, as the apps write it. */
export function backup(rows: RowSnapshot[], tripId: string): TripExport {
  const tables: TripExport['tables'] = {};
  let title = '';
  for (const r of rows) {
    if (r.values.deletedAt != null) continue;
    const row: Record<string, string | number | boolean | null> = { id: r.id, ownerUserId: r.ownerUserId };
    for (const [k, v] of Object.entries(r.values)) if (!DROPPED.has(k)) row[k] = v;
    const list = tables[r.table] ?? [];
    list.push(row);
    tables[r.table] = list;
    if (r.table === 'trip') title = String(r.values.title ?? '');
  }
  return {
    format: 'pathfinder-export',
    version: 1,
    schema: SCHEMA_VERSION,
    exportedAt: stampNow(),
    tripId,
    title,
    tables,
  };
}

/** A backup as pushes of at most MAX_PUSH_CHANGES, trip first; ids are kept, so it merges. */
export function restoreEdits(data: unknown): Edit[][] {
  const x = data as Partial<TripExport>;
  if (
    x?.format !== 'pathfinder-export' ||
    typeof x.version !== 'number' ||
    x.version > 1 ||
    !x.tripId ||
    !x.tables
  )
    throw new Error("That file isn't a Pathfinder backup.");
  const skip = new Set(['id', 'ownerUserId', 'createdAt', 'updatedBy']);
  const order = [
    'trip',
    ...Object.keys(x.tables)
      .filter((t) => t !== 'trip')
      .sort(),
  ];
  const edits: Edit[] = [];
  for (const table of order)
    for (const row of x.tables[table] ?? []) {
      if (typeof row.id !== 'string') continue;
      const fields = Object.fromEntries(Object.entries(row).filter(([k]) => !skip.has(k)));
      if (Object.keys(fields).length === 0) continue;
      edits.push({
        table,
        id: row.id,
        tripId: x.tripId,
        ownerUserId: typeof row.ownerUserId === 'string' ? row.ownerUserId : null,
        fields,
      });
    }
  const chunks: Edit[][] = [];
  for (let i = 0; i < edits.length; i += Math.min(MAX_PUSH_CHANGES, 50))
    chunks.push(edits.slice(i, i + Math.min(MAX_PUSH_CHANGES, 50)));
  return chunks;
}

/** Saves text as a file in the browser. */
export function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const slug = (title: string) =>
  title
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^\p{L}\p{N}-]/gu, '') || 'trip';
